/**
 * Stops a settings screen from silently discarding unsaved edits.
 *
 * Two exits, two mechanisms:
 *
 * - **In-app navigation** — the router blocker holds the navigation and this
 *   component answers with the product's own ConfirmDialog. `window.confirm`
 *   would work too, but it is the browser's dialog: unstyled, untranslatable,
 *   and (in an embedded iframe) blocking for the host page as well.
 * - **Tab close / reload** — `beforeunload`, where the browser insists on its
 *   own generic wording and all a page may do is opt in. Handed to the same
 *   blocker via `enableBeforeUnload` so both paths share one condition.
 *
 * Two kinds of edit, and they exit differently:
 *
 * - **The shared form** (`isDirty`) spans every section of the screen. It
 *   survives a section switch, so navigation WITHIN the same pathname is not
 *   blocked for it — the project settings screen syncs its active section to
 *   `?section=` and nothing is lost moving between them. Asking there would
 *   teach the user to dismiss the dialog without reading it, which is how a
 *   guard stops guarding.
 * - **Section-local forms** — `TeamTab`, `AlertChannelsTab`,
 *   `ChannelSettingsTab` each keep their edits in their own component state
 *   with their own Save. Those edits are invisible to `isDirty`, and they do
 *   NOT survive a section switch: the tab unmounts and the state goes with it.
 *   A section reports itself through `useReportUnsavedSection`.
 *
 * So the rule is: leaving the screen is guarded by either kind of edit;
 * switching sections is guarded only by the kind that a switch would destroy.
 *
 * And a third exit the router cannot see at all: switching ORGANIZATION. The
 * sidebar calls `selectOrg` directly, so no navigation happens for a blocker
 * to hold — the screen is simply replaced by a skeleton while the new org
 * loads, taking the form with it. `UnsavedWorkProvider` sits in `AppShell`
 * above the sidebar so the switcher can ask `useHasUnsavedWork()` and put up
 * its own confirmation.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useBlocker } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/modals';

/**
 * Which kind of edit a reporter is holding.
 *
 * - `shared` — the screen-wide form. Survives a section switch.
 * - `section` — a form living in one section's own state. A section switch
 *   unmounts it, so the switch itself destroys the edit.
 */
type UnsavedScope = 'shared' | 'section';

interface UnsavedWorkRegistry {
  readonly dirtyKeys: ReadonlyMap<string, UnsavedScope>;
  readonly report: (key: string, isDirty: boolean, scope: UnsavedScope) => void;
}

/**
 * Absent by default on purpose: a screen rendered outside the shell (embedded
 * placements, tests) should not have to provide anything, and the hooks below
 * become no-ops rather than crashing.
 */
const UnsavedWorkContext = createContext<UnsavedWorkRegistry | null>(null);

/**
 * Mounted in `AppShell`, ABOVE the sidebar — deliberately, and that is the
 * whole reason this is a context rather than screen-local state. Switching
 * organization is not a router navigation: the sidebar calls `selectOrg`
 * directly, `isOrgSwitching` swaps the screen for a skeleton, and the form
 * unmounts. The router blocker never sees it, so the sidebar has to ask the
 * question itself — and it can only ask if the answer lives above it.
 */
export function UnsavedWorkProvider({ children }: { children: React.ReactNode }) {
  const [dirtyKeys, setDirtyKeys] = useState<ReadonlyMap<string, UnsavedScope>>(
    () => new Map()
  );

  const report = useCallback((key: string, isDirty: boolean, scope: UnsavedScope) => {
    setDirtyKeys((previous) => {
      // Returning the same Map when nothing changed keeps a reporter that
      // re-reports the same value from re-rendering the whole shell.
      const had = previous.get(key);
      if ((isDirty ? scope : undefined) === had) return previous;
      const next = new Map(previous);
      if (isDirty) next.set(key, scope);
      else next.delete(key);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ dirtyKeys, report }), [dirtyKeys, report]);

  return (
    <UnsavedWorkContext.Provider value={value}>{children}</UnsavedWorkContext.Provider>
  );
}

function useReportUnsavedWork(key: string, isDirty: boolean, scope: UnsavedScope): void {
  const report = useContext(UnsavedWorkContext)?.report;

  useEffect(() => {
    if (!report) return;
    report(key, isDirty, scope);
    // Clearing on unmount is what makes it honest: once the user confirmed the
    // discard and the form is gone, its edits must stop blocking the next exit.
    return () => report(key, false, scope);
  }, [report, key, isDirty, scope]);
}

/**
 * Publish a section-local form's dirty state — `TeamTab`, `AlertChannelsTab`,
 * `ChannelSettingsTab` and anything else that saves itself.
 */
export function useReportUnsavedSection(section: string, isDirty: boolean): void {
  useReportUnsavedWork(section, isDirty, 'section');
}

/**
 * "Is anything unsaved anywhere on screen." For exits the router cannot see —
 * today, the organization switcher.
 */
export function useHasUnsavedWork(): boolean {
  return (useContext(UnsavedWorkContext)?.dirtyKeys.size ?? 0) > 0;
}

export interface UnsavedChangesGuardProps {
  /** The screen-wide shared form has edits the server has not seen. */
  isDirty: boolean;
}

export function UnsavedChangesGuard({ isDirty }: UnsavedChangesGuardProps) {
  const { t } = useTranslation(['common']);
  const dirtyKeys = useContext(UnsavedWorkContext)?.dirtyKeys;
  const hasSectionEdits = dirtyKeys ? [...dirtyKeys.values()].includes('section') : false;
  const hasAnyEdits = isDirty || hasSectionEdits;
  // Report the screen-wide form upward too, so the organization switcher —
  // which exits without a router navigation — sees the same truth this
  // component does.
  useReportUnsavedWork('screen:shared-form', isDirty, 'shared');

  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (!hasAnyEdits) return false;
      if (current.pathname !== next.pathname) return true;
      // Same pathname means a section switch. The shared form survives it;
      // a section-local form is unmounted by it.
      return hasSectionEdits;
    },
    enableBeforeUnload: () => hasAnyEdits,
    disabled: !hasAnyEdits,
    withResolver: true,
  });

  if (blocker.status !== 'blocked') return null;

  return (
    <div data-testid="unsaved-changes-dialog">
      <ConfirmDialog
        isOpen
        variant="warning"
        title={t('common:modals.unsavedChanges', 'Unsaved changes')}
        message={t(
          'common:modals.unsavedChangesLeaveMessage',
          'You have unsaved changes on this page. If you leave now, they will be lost.'
        )}
        confirmText={t('common:modals.discardChanges', 'Discard changes')}
        cancelText={t('common:modals.keepEditing', 'Keep editing')}
        onConfirm={() => blocker.proceed()}
        onClose={() => blocker.reset()}
      />
    </div>
  );
}
